import {PencilIcon} from '@primer/octicons-react'
import {Textarea} from '@primer/react'
import {clsx} from 'clsx'
import {useEffect, useRef, useState} from 'react'

import styles from './InlineEditableText.module.css'
import {InlineEditActions} from './InlineEditActions'

interface InlineEditableTextProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  maxLength?: number | null
  defaultEditing?: boolean
  ariaPrefix?: string
}

export function InlineEditableText({
  value,
  onChange,
  maxLength = null,
  placeholder = '',
  className = '',
  defaultEditing = false,
  // This will be prepended to the aria label for additional clarity.
  ariaPrefix = '',
}: InlineEditableTextProps) {
  const [isEditing, setIsEditing] = useState(defaultEditing)
  const [draft, setDraft] = useState(value)
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null)
  const readRef = useRef<HTMLButtonElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Close editing mode when clicking outside the component
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isEditing) return

      // Check if the click was outside our container
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsEditing(false)
        onChange(draft.trim())
      }
    }

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isEditing, draft, containerRef, onChange])

  // Measure height of read-mode content to prevent layout shifts
  useEffect(() => {
    if (readRef.current) {
      setMeasuredHeight(readRef.current.offsetHeight)
    }
  }, [value])

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus()
      textareaRef.current.style.height = `${measuredHeight}px`
    }
  }, [isEditing, measuredHeight])

  const handleSave = () => {
    setIsEditing(false)
    onChange(draft.trim())
  }

  const handleCancel = () => {
    setIsEditing(false)
    setDraft(value)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- This text editor only handles its local save gesture.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSave()
    }
  }

  return (
    <div ref={containerRef} className={clsx(styles.container, className)}>
      {isEditing ? (
        <Textarea
          ref={textareaRef}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={styles.textarea}
          style={{height: measuredHeight || 'auto'}}
          resize="none"
        />
      ) : (
        <button
          ref={readRef}
          onClick={() => setIsEditing(true)}
          aria-label={`${ariaPrefix} ${value.trim() || placeholder}. Activate to edit.`.trim()}
          className={styles.readModeContainer}
        >
          <span className={clsx(styles.readModeText, className)}>
            {value?.trim() || <span className={styles.placeholder}>{placeholder}</span>}
          </span>
          <PencilIcon className={styles.editIcon} size={16} />
        </button>
      )}
      {isEditing && (
        <InlineEditActions value={draft} maxLength={maxLength} onSave={handleSave} onCancel={handleCancel} />
      )}
    </div>
  )
}
